import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import avatarFB from '@/assets/images/avatar-fb.png';
import avatarIG from '@/assets/images/avatar-ig.png';
import avatarLinkedIn from '@/assets/images/avatar-linkedin.png';
import avatarEmail from '@/assets/images/email-icon.png';
import mergedIcon from '@/assets/images/merged-icon.png';
import avatarSMS from '@/assets/images/sms-icon.png';
import useInboxStore from '@/stores/InboxStore';
import { useHotkeys } from '@blueprintjs/core';
import { ReloadIcon } from '@radix-ui/react-icons';
import { intervalToDuration, parseISO } from 'date-fns';
import { CheckSquare, Loader2Icon, Square } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import {
  trackContactFocus,
  trackConversationFetch,
  trackRefreshConversationButtonClicked,
} from '@/lib/analytics-event';
import { cn, isMergedChannel } from '@/lib/utils';
import { fetchConversations } from '@/hooks/inbox';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Card, CardContent } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import BulkArchiveChatButton from './BulkArchiveChatButton';

const ConversationsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ref: inViewRef, inView } = useInView();
  const [messageIsHovered, setMessageIsHovered] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);

  const { conversations, setConversations, setNextConversations } =
    useInboxStore(
      useShallow((state) => ({
        conversations: state.conversations,
        setConversations: state.setConversations,
        setNextConversations: state.setNextConversations,
      }))
    );

  const page = useInboxStore((state) => state.page);

  const {
    selectedTags,
    selectedOwners,
    selectedChannels,
    searchText,
    selectedConversationStatus,
    selectedSortOrder,
    focusedIndex,
    setFocusedIndex,
    conversationListUpdateStatusCode,
    setConversationListUpdateStatusCode,
    socketUpdateCount,
    resetSocketCount,
    isMultiSelectMode,
    toggleConversationSelection,
    selectedConversations,
  } = useInboxStore(
    useShallow((state) => ({
      selectedTags: state.selectedTags,
      selectedOwners: state.selectedOwners,
      selectedChannels: state.selectedChannels,
      searchText: state.searchText,
      selectedConversationStatus: state.selectedConversationStatus,
      selectedSortOrder: state.selectedSortOrder,
      focusedIndex: state.focusedIndex,
      setFocusedIndex: state.setFocusedIndex,
      conversationListUpdateStatusCode: state.conversationListUpdateStatusCode,
      setConversationListUpdateStatusCode:
        state.setConversationListUpdateStatusCode,
      socketUpdateCount: state.socketUpdateCount,
      resetSocketCount: state.resetSocketCount,
      isMultiSelectMode: state.isMultiSelectMode,
      toggleConversationSelection: state.toggleConversationSelection,
      selectedConversations: state.selectedConversations,
    }))
  );

  // Create a memoized selector for checking if a conversation is selected
  const checkConversationSelected = useCallback(
    (conversationId) => {
      return selectedConversations.includes(conversationId);
    },
    [selectedConversations]
  );

  const { pathname } = location;

  const defaultPic = `https://files.sbccrm.com/sbccrm/sbccrm/contacts/pics/no-pic.jpg`;

  const fetchData = useCallback(
    async (type, page) => {
      try {
        const response = await fetchConversations(
          {
            tags: selectedTags,
            agents: selectedOwners,
            channel: selectedChannels,
            searchText: searchText,
          },
          page,
          selectedConversationStatus,
          selectedSortOrder
        );
        if (type === 'new') {
          const { data, next_page_url, current_page, total } = response;
          setConversations(
            data,
            next_page_url ? current_page + 1 : null,
            total
          );

          if (isContactOpen) {
            navigate('/inbox');
          }
          setConversationListUpdateStatusCode(0);
        } else {
          const { data, next_page_url, current_page, total } = response;
          setNextConversations(
            data,
            next_page_url ? current_page + 1 : null,
            total
          );

          setConversationListUpdateStatusCode(1);
        }
      } catch (error) {
        console.error('error fetching conversations:', error);
      }
    },
    [
      navigate,
      setConversations,
      setNextConversations,
      selectedTags,
      selectedOwners,
      selectedChannels,
      searchText,
      selectedConversationStatus,
      selectedSortOrder,
      setConversationListUpdateStatusCode,
    ]
  );

  const handleSocketCountRefresh = () => {
    fetchData('new', 1);
    trackRefreshConversationButtonClicked();
    resetSocketCount();
  };

  useEffect(() => {
    fetchData('new', 1);
    trackConversationFetch(
      searchText,
      selectedConversationStatus.value,
      selectedSortOrder,
      selectedTags,
      selectedOwners
    );
    resetSocketCount();
  }, [
    setConversations,
    navigate,
    fetchData,
    selectedTags,
    selectedOwners,
    searchText,
    selectedConversationStatus,
    selectedSortOrder,
    resetSocketCount,
  ]);

  useEffect(() => {
    if (inView) {
      console.log('in view');
      fetchData('next', page);
    }
  }, [inView, fetchData]);

  // TODO: Fix the time diff error identified in prod
  const getTimeDiff = (timestamp) => {
    const date = parseISO(timestamp);
    const duration = intervalToDuration({ start: date, end: new Date() });

    const {
      years = 0,
      months = 0,
      days = 0,
      hours = 0,
      minutes = 0,
      seconds = 0,
    } = duration;

    const totalHours = years * 8760 + months * 730 + days * 24 + hours;
    const totalMinutes = totalHours * 60 + minutes;
    const totalSeconds = totalMinutes * 60 + seconds;

    let timeString = '';
    if (totalHours >= 48) {
      if (duration.years > 0) {
        timeString = `${duration.years}y`;
      } else if (duration.months > 0) {
        timeString = `${duration.months}mth`;
      } else {
        timeString = `${duration.days}d`;
      }
    } else if (totalHours >= 1) {
      timeString = `${totalHours}h`;
    } else if (totalMinutes >= 1) {
      timeString = `${totalMinutes}m`;
    } else {
      timeString = `${totalSeconds}s`;
    }

    return timeString;
  };

  const cardRefs = useRef([]);

  const handleKeyDownScroll = (event) => {
    if (
      event.altKey &&
      (event.key === 'ArrowUp' || event.key === 'ArrowDown')
    ) {
      event.preventDefault();
    }
  };

  const handleBasicKeyPress = (event, index) => {
    if (event.key === 'Enter') {
      setIsContactOpen(true);
      navigate(`/inbox/${conversations[index]._id}`, { replace: true });
    }

    if (event.key === 'Escape') {
      event.target.blur();
    }
  };

  const { id } = useParams();

  useEffect(() => {
    if (conversationListUpdateStatusCode === 0) {
      const index = conversations.findIndex((conversation) => {
        return conversation._id == id;
      });
      setFocusedIndex(index == -1 ? 0 : index);
    }
  }, [id, conversations, setFocusedIndex, conversationListUpdateStatusCode]);

  useEffect(() => {
    if (conversationListUpdateStatusCode == 1) {
      setConversationListUpdateStatusCode(2);
    }
  }, [conversationListUpdateStatusCode, setConversationListUpdateStatusCode]);

  const handleUpKeyPress = useCallback(() => {
    if (focusedIndex > 0) {
      cardRefs.current[focusedIndex - 1].focus();

      setFocusedIndex(focusedIndex - 1);
      trackContactFocus('keyboard');
    }
  }, [focusedIndex, setFocusedIndex]);

  const handleDownKeyPress = useCallback(() => {
    if (focusedIndex < cardRefs.current.length - 1) {
      cardRefs.current[focusedIndex + 1].focus();
      setFocusedIndex(focusedIndex + 1);
      trackContactFocus('keyboard');
    }
  }, [focusedIndex, setFocusedIndex]);

  const hotkeys = useMemo(
    () => [
      {
        combo: 'alt + arrowup',
        global: true,
        label: 'Move Conversation - Up',
        onKeyUp: handleUpKeyPress,
      },
      {
        combo: 'alt + arrowdown',
        global: true,
        label: 'Move Conversation - Down',
        onKeyUp: handleDownKeyPress,
      },
    ],
    [handleUpKeyPress, handleDownKeyPress]
  );
  const { handleKeyDown, handleKeyUp } = useHotkeys(hotkeys);

  return (
    <>
      <nav className='m-0 flex  space-x-2   overflow-hidden   lg:flex-col lg:space-x-0 lg:space-y-1'>
        {/* Sticky archive button - shows when in multi-select mode with selections */}
        {isMultiSelectMode && (
          <div className='sticky top-0 z-10 bg-background'>
            <BulkArchiveChatButton />
          </div>
        )}

        <ScrollArea onKeyDown={handleKeyDownScroll}>
          <ul role='list' className='m-[1px] space-y-[2px]'>
            {socketUpdateCount > 0 && (
              <li
                className='flex cursor-pointer flex-col items-center justify-center border bg-primary py-2 text-xs text-card-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0'
                onMouseEnter={() => setMessageIsHovered(true)}
                onMouseLeave={() => setMessageIsHovered(false)}
                onClick={handleSocketCountRefresh}
              >
                {messageIsHovered ? (
                  <div className='flex items-center justify-center space-x-3'>
                    <ReloadIcon />
                    <span>Click to refresh</span>
                  </div>
                ) : (
                  <span>
                    {socketUpdateCount} new event
                    {socketUpdateCount > 1 ? 's' : ''}
                  </span>
                )}
              </li>
            )}
            {conversations.map((contact, index) => (
              <li
                key={index}
                ref={(el) => {
                  const isLastItem = index === conversations.length - 1;
                  if (isLastItem && page !== null && page !== 0) {
                    inViewRef(el);
                  }

                  cardRefs.current[index] = el;
                }}
                tabIndex={0}
                onKeyDown={(event) => handleBasicKeyPress(event, index)}
                className='ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0'
              >
                <Link
                  to={isMultiSelectMode ? '#' : `/inbox/${contact._id}`}
                  onClick={(e) => {
                    if (isMultiSelectMode) {
                      e.preventDefault();
                      toggleConversationSelection(contact._id);
                    } else {
                      setIsContactOpen(true);
                      trackContactFocus('mouse');
                    }
                  }}
                  replace
                >
                  <Card
                    className={cn(
                      pathname === `/inbox/${contact._id}` && !isMultiSelectMode
                        ? 'bg-secondary'
                        : '',
                      checkConversationSelected(contact._id) &&
                        isMultiSelectMode
                        ? 'border-primary bg-primary/10'
                        : '',
                      'rounded-none border-none hover:bg-secondary'
                    )}
                  >
                    <CardContent className='grid px-2 py-3'>
                      <div className='flex min-w-0 items-center justify-between space-x-4'>
                        <div className='flex min-w-0 items-start space-x-4'>
                          {/* Checkbox for multi-select */}
                          {isMultiSelectMode && (
                            <div
                              className='flex cursor-pointer items-center pt-1'
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleConversationSelection(contact._id);
                              }}
                            >
                              {checkConversationSelected(contact._id) ? (
                                <CheckSquare className='h-4 w-4 text-primary' />
                              ) : (
                                <Square className='h-4 w-4 text-muted-foreground' />
                              )}
                            </div>
                          )}

                          <div className='relative'>
                            {contact.channel === 'instagram' ? (
                              <img
                                src={avatarIG}
                                className='absolute -bottom-1 -right-1 z-50 aspect-square h-4 w-4 '
                              />
                            ) : // <InstagramIcon className='absolute bottom-0 left-0 z-50 h-4 w-4 bg-primary text-primary-foreground' />
                            contact.channel === 'facebook' ? (
                              // <FacebookIcon className='absolute bottom-0 left-0 z-50 h-4 w-4 bg-primary text-primary-foreground' />
                              <img
                                src={avatarFB}
                                className='absolute -bottom-1 -right-1 z-50 aspect-square h-4 w-4 '
                              />
                            ) : contact.channel === 'linkedin' ? (
                              // <LinkedInIcon className='absolute bottom-0 left-0 z-50 h-4 w-4 bg-primary text-primary-foreground' />
                              <img
                                src={avatarLinkedIn}
                                className='absolute -bottom-1 -right-1 z-50 aspect-square h-4 w-4 '
                              />
                            ) : contact.channel === 'sms' ? (
                              <img
                                src={avatarSMS}
                                className='absolute -bottom-1 -right-1 z-50 aspect-square h-5 w-5 '
                              />
                            ) : contact.channel === 'email' ? (
                              <img
                                src={avatarEmail}
                                className='absolute -bottom-1 -right-1 z-50 aspect-square h-4 w-4 '
                              />
                            ) : isMergedChannel(contact.channel) ? (
                              // Merged channel icon - using SMS icon for now
                              <img
                                src={mergedIcon}
                                className='absolute -bottom-1 -right-1 z-50 aspect-square h-5 w-5 '
                              />
                            ) : null}
                            {contact.channel === 'sms' ||
                            isMergedChannel(contact.channel) ? (
                              <div className='relative h-8 w-8 shrink-0 overflow-hidden rounded-full'>
                                {contact.profile_picture_url ? (
                                  <img
                                    src={contact.profile_picture_url}
                                    className='h-full w-full object-cover'
                                  />
                                ) : contact.full_name ? (
                                  <div className='absolute inset-0 flex items-center justify-center rounded-full bg-avatar text-sm text-primary'>
                                    {contact.full_name
                                      ?.charAt(0)
                                      ?.toUpperCase() || ''}
                                  </div>
                                ) : (
                                  <img
                                    src='https://files.sbccrm.com/sbccrm/sbccrm/contacts/pics/no-pic.jpg'
                                    className='h-full w-full object-cover'
                                  />
                                )}
                              </div>
                            ) : (
                              <Avatar>
                                {/*
                                  Derive a safe display name and initial so we don't
                                  try to index into null/undefined full_name.
                                */}
                                {(() => {
                                  const displayName =
                                    contact.full_name ||
                                    contact.ig_username ||
                                    contact.linkedin_id ||
                                    contact.email ||
                                    'Unknown';
                                  const initial =
                                    (displayName &&
                                      displayName
                                        .trim()
                                        .charAt(0)
                                        .toUpperCase()) ||
                                    '?';
                                  return (
                                    <>
                                      <AvatarImage
                                        src={
                                          contact.profile_picture_url ||
                                          'https://files.sbccrm.com/sbccrm/sbccrm/contacts/pics/no-pic.jpg'
                                        }
                                      />
                                      <AvatarFallback className='bg-primary-foreground text-primary'>
                                        {initial}
                                      </AvatarFallback>
                                    </>
                                  );
                                })()}
                              </Avatar>
                            )}
                          </div>
                          <div className='min-w-0 space-y-0'>
                            <p className=' truncate pb-1.5 text-sm font-medium leading-none'>
                              {contact.full_name}
                            </p>

                            {contact.is_latest_message_deleted ? (
                              <p className='text-sm italic text-muted-foreground/75'>
                                This message was deleted
                              </p>
                            ) : (
                              <p className='truncate text-sm text-muted-foreground'>
                                {contact.latest_message_text}
                              </p>
                            )}
                            {contact.tags && contact.tags.length > 0 && (
                              <div className='flex flex-wrap gap-1 pt-1'>
                                {contact.tags
                                  .filter((tag) => tag.pipeline_tag)
                                  .map((tag, tagIndex) => (
                                    <span
                                      key={tagIndex}
                                      className='inline-flex items-center rounded-sm px-2 py-0.5 text-xs'
                                      style={{
                                        backgroundColor: tag.colour + '20',
                                        color: tag.colour,
                                      }}
                                    >
                                      {tag.name}
                                    </span>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <span className='text-xs opacity-50'>
                          {getTimeDiff(contact.latest_message_timestamp)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
          {page !== null && page !== 0 && (
            <div className='flex items-center justify-center py-3'>
              <Loader2Icon className='h-4 w-4 animate-spin' />
            </div>
          )}
        </ScrollArea>
      </nav>
    </>
  );
};

export default ConversationsList;
